import { Box, Stack, TextField, Typography } from "@mui/material";
import { FC, useMemo, useRef, useState } from "react";
import { TPagination } from "@/types/api";
import { FilterConditionToolbar, TSavedCondition as TToolbarSavedCondition } from "@irohana-dev/components";
import { TalentFilterConditionPanel } from "@/features/talent/components/talent-list/TalentFilterConditionPanel";
import {
  TTalentFilterFormExtended,
  TTalentDisplayColumnSetting,
  defaultTalentDisplayColumns,
  defaultTalentFilterValues,
  isEmptyTalentFilterValue,
  normalizeTalentDateFields,
  normalizeTalentMultiSelectFields,
} from "@/features/talent/components/talent-list/talent-filter-shared";
import { applyColumnSettings } from "@/features/talent/components/talent-list/talent-filter-condition-adapter";
import { TALENT_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/talent/constants/common";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { typography } from "@/theme/typography";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { SelectedTalentPagesPopover, TTalentSelectionProps } from "@/features/talent/components/talent-list/SelectedTalentPagesPopover";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";
import { hasDisplayColumnsChanged } from "@/components/commons/filter-condition/filter-condition-adapter-kit";


type Props = {
  /** Grouped props for talent selection state to reduce props drilling */
  selectionProps: TTalentSelectionProps;
  pagination?: TPagination;
  onSearchFullText?: (keyword: string) => void; // [TEST-ONLY]
  onApplyAdvancedFilters?: (filters: TTalentFilterFormExtended) => void;
  onApplyDisplaySettings?: (columns: TTalentDisplayColumnSetting[]) => void;
  appliedConditionName?: string;
  appliedConditionId?: number | null;
  onAppliedConditionNameChange?: (name: string) => void;
  onAppliedConditionIdChange?: (id: number | null) => void;
  currentDisplayColumns?: TTalentDisplayColumnSetting[] | null;
  currentAdvancedFilters?: TTalentFilterFormExtended | null;
};

export const TalentListTop: FC<Props> = ({
  selectionProps,
  pagination,
  onSearchFullText,
  onApplyAdvancedFilters,
  onApplyDisplaySettings,
  appliedConditionName,
  appliedConditionId,
  onAppliedConditionNameChange,
  onAppliedConditionIdChange,
  currentDisplayColumns,
  currentAdvancedFilters,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const { data: savedConditionsData } = useSavedConditionList("talent");

  const handleOpenCreateFilter = () => {
    setFilterMode("create");
    setFilterAnchorEl(document.body);
  };

  const handleOpenEditFilter = () => {
    setFilterMode("edit");
    setFilterAnchorEl(document.body);
  };

  const handleFilterPopoverClose = () => {
    setFilterAnchorEl(null);
  };

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const justSavedConditionRef = useRef(false);

  const handleApplyFiltersFromPanel = (filters: TTalentFilterFormExtended) => {
    onApplyAdvancedFilters?.(filters);
    if (justSavedConditionRef.current) {
      justSavedConditionRef.current = false;
      return;
    }
    if (filterMode === "create") {
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    }
  };

  const handleConditionSaved = (id: number, title: string) => {
    justSavedConditionRef.current = true;
    onAppliedConditionNameChange?.(title);
    onAppliedConditionIdChange?.(id);
    setFilterMode("edit");
  };

  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const activeFilterCount = useMemo(() => {
    if (!currentAdvancedFilters) return 0;
    const { registrationDateFrom, registrationDateTo, ...rest } = currentAdvancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyTalentFilterValue(v)).length;
    if (registrationDateFrom || registrationDateTo) count += 1;
    return count;
  }, [currentAdvancedFilters]);

  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const deleteSavedCondition = useDeleteSavedCondition();

  const savedConditions = useMemo<TToolbarSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        id: Number(item.id),
        name: item.title,
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  const applySavedColumnSettings = (picked: { includeColumnSettings?: boolean; columnSettings?: unknown }) => {
    if (!picked.includeColumnSettings || !picked.columnSettings) return;
    const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
    const base = currentDisplayColumns ?? [...defaultTalentDisplayColumns];
    const next = applyColumnSettings(base, settings.order ?? [], settings.hidden ?? []);
    onApplyDisplaySettings?.(next);
    localStorage.setItem(TALENT_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(next));
  };

  const handleSelectCondition = (id: string | number) => {
    const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
    if (!picked) return;
    onApplyAdvancedFilters?.({
      ...defaultTalentFilterValues,
      ...normalizeTalentMultiSelectFields(normalizeTalentDateFields(picked.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TTalentFilterFormExtended);
    applySavedColumnSettings(picked);
    onAppliedConditionNameChange?.(picked.title);
    onAppliedConditionIdChange?.(Number(picked.id));
  };

  const handleDeleteAppliedCondition = async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      onApplyAdvancedFilters?.({ ...defaultTalentFilterValues });
      onAppliedConditionNameChange?.("");
      onAppliedConditionIdChange?.(null);
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleClearAppliedFilter = () => {
    onApplyAdvancedFilters?.({ ...defaultTalentFilterValues });
    onAppliedConditionNameChange?.("");
    onAppliedConditionIdChange?.(null);
    onApplyDisplaySettings?.([...defaultTalentDisplayColumns]);
    localStorage.setItem(TALENT_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(defaultTalentDisplayColumns));
    setIsClearConfirmOpen(false);
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body14Semi">
              全 {formatTotal(pagination?.total) ?? 0} 件中 {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 20), pagination?.total ?? 0)}{" "}
              件を表示
            </Typography>
            <Typography variant="body14Semi">│</Typography>
            <SelectedTalentPagesPopover {...selectionProps} />
          </Stack>

          <FilterConditionToolbar
            onOpenFilter={handleOpenCreateFilter}
            activeCount={
              activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(currentDisplayColumns, defaultTalentDisplayColumns))
            }
            conditions={savedConditions}
            selectedId={appliedConditionId ?? undefined}
            onSelect={handleSelectCondition}
            onClearSelection={() => setIsClearConfirmOpen(true)}
            onEdit={handleOpenEditFilter}
            onDelete={() => setIsDeleteConfirmOpen(true)}
            shareColor={filterConditionShareColor}
          />

          {/* [TEST-ONLY] full-text search — Enter to search. Remove when done testing. */}
          {onSearchFullText && (
            <TextField
              size="small"
              placeholder="キーワード検索（テスト）"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSearchFullText((e.target as HTMLInputElement).value);
                }
              }}
              sx={{ minWidth: 320, "& .MuiInputBase-input": { ...typography.body14, py: 0.75 } }}
            />
          )}
        </Stack>
      </Box>

      <TalentFilterConditionPanel
        mode={filterMode}
        appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
        appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
        isOpen={Boolean(filterAnchorEl)}
        onClose={handleFilterPopoverClose}
        onApplyFilters={handleApplyFiltersFromPanel}
        onApplyDisplaySettings={onApplyDisplaySettings}
        initialDisplayColumns={currentDisplayColumns || undefined}
        initialFilters={filterMode === "edit" ? currentAdvancedFilters || undefined : defaultTalentFilterValues}
        appliedConditionShareScope={appliedCondition?.shareScope}
        appliedConditionIsDefault={appliedCondition?.isDefault}
        appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
        appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
        onConditionSaved={handleConditionSaved}
      />
      <ConfirmDeleteModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onOk={handleDeleteAppliedCondition}
        message="この条件を削除してもよろしいですか？"
        title=""
      />
      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearAppliedFilter}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </>
  );
};
